import { useCallback, useEffect, useRef } from 'react';
import type { PageState } from '@roll1/shared';
import { useSessionContext } from '../contexts/SessionContext';
import { useCanvasContext } from '../contexts/CanvasContext';
import { useTokenContext } from '../contexts/TokenContext';

/**
 * Page save/load primitives — WITHOUT the viewingPageId switch effect.
 *
 * For code that must put a page into the canvas hooks but must not own page
 * switching: the session-level join hydration (useSessionStoreSync, mounted
 * above the StudioGate fork), and a GM Studio map pane (its own hooks, its own
 * page — PageViewScope gives it its own `hydratedPageIdRef`). Mounting the
 * switch effect there as well would double-save / double-load on every switch
 * of the surface below.
 *
 * `loadPageToHooks` records the loaded page in `pages.hydratedPageIdRef`;
 * `saveCurrentPageToStore` writes the hooks back into THAT page, i.e. the page
 * whose data the hooks actually hold (viewingPageId can already point at the
 * next page before the switch effect runs). '' = the hooks hold nothing that
 * may be saved.
 */
export function usePageLoader() {
  const { pages } = useSessionContext();
  const { engine, drawing, fog, dynamicFog, wallsHook, locationLinks, mapObjects } = useCanvasContext();
  const { tokens, notes: notesHook, pins: pinsHook } = useTokenContext();

  // Vestigial: FogManager now owns its own fogSentEnabled; kept only so
  // useMultiplayerSync's return shape is unchanged (test mocks reference it).
  const fogSentEnabled = useRef(false);

  /**
   * Save current hook state back into the page data store. `keepLists`: the tokens, notes and pins are React state —
   * right after a load they still hold the previous lists until the next render, so a save then keeps the stored lists
   * (30.10: a Studio map pane unmounted before its first render — StrictMode, a fast tab switch — saved no tokens and
   * reloaded the page empty).
   */
  const saveCurrentPageToStore = useCallback((opts: { keepLists?: boolean } = {}) => {
    const pid = pages.hydratedPageIdRef.current;
    if (!pid) return;
    const existing = pages.getPageData(pid);
    if (!existing) return;
    const saved: any = {
      ...existing,
      tokens: opts.keepLists ? existing.tokens : tokens.tokens.map((t) => {
        const { imageEl, ...rest } = t;
        return rest;
      }),
      drawings: drawing.strokes.current,
      fog: { enabled: fog.enabled.current, mode: fog.fogModeRef.current, revealed: fog.revealed.current, closings: fog.closings.current, explored: dynamicFog.exploredRef.current },
      notes: opts.keepLists ? existing.notes : notesHook.notes,
      pins: opts.keepLists ? existing.pins : pinsHook.pins,
      mapObjects: mapObjects.objects.current,
      walls: wallsHook.walls.current,
    };
    // Preserve bgColor from engine ref (may have been set via color picker)
    if (engine.bgColorRef.current) saved.bgColor = engine.bgColorRef.current;
    pages.setPageData(pid, saved);
  }, [pages, tokens.tokens, drawing, fog, notesHook.notes, pinsHook.pins, wallsHook.walls]);

  /**
   * Load page data from store into hooks. Resolves when the page's map image
   * has loaded and the camera was fitted to it (a Studio pane restores its
   * own pan/zoom after that).
   */
  const loadPageToHooks = useCallback(
    (page: PageState): Promise<void> => {
      tokens.setTokens(page.tokens.map((t) => ({ ...t, imageEl: null })));
      drawing.strokes.current = [...page.drawings];
      fog.enabled.current = page.fog.enabled;
      fog.revealed.current = [...page.fog.revealed];
      fog.closings.current = [...(page.fog.closings ?? [])];
      fog.fogModeRef.current = page.fog.mode ?? 'manual';
      fogSentEnabled.current = page.fog.enabled;
      notesHook.setNotes(page.notes ?? []);
      pinsHook.setPins(page.pins ?? []);
      wallsHook.walls.current = [...(page.walls ?? [])];
      locationLinks.setLinks(page.locationLinks ?? []);
      mapObjects.setObjects(page.mapObjects ?? []);
      engine.bgColorRef.current = (page as any).bgColor || null;
      // Dynamic fog: sync explored and invalidate caches
      dynamicFog.setExplored([...(page.fog.explored ?? [])]);
      dynamicFog.invalidateAll();
      const mapLoaded = engine.loadMap(page.mapUrl ? page.mapUrl : '');
      fog.invalidateCache();
      drawing.invalidateCache();
      engine.markAllDirty();
      pages.hydratedPageIdRef.current = page.id;
      return mapLoaded;
    },
    [pages, tokens, drawing, fog, dynamicFog, engine, notesHook, pinsHook, locationLinks, mapObjects],
  );

  return { saveCurrentPageToStore, loadPageToHooks, fogSentEnabled };
}

/**
 * Page hydration — the save/load/switch mechanism that makes the canvas display
 * a given page's data. Extracted VERBATIM from useMultiplayerSync (2026-07-29)
 * so any surface that hosts the canvas can reuse it: the table (via
 * useMultiplayerSync, which still owns the canvas WS router) and — for GM Studio
 * S4 iteration 2 — the studio focus pane.
 *
 * Purely client-side: no WebSocket traffic. Switching `pages.viewingPageId`
 * saves the previous page's hook state into the page store and loads the new
 * page's data into the canvas hooks. All inputs come from contexts that live
 * ABOVE the StudioGate table/studio fork, so this hook works in either branch.
 *
 * "Previous page" is `pages.hydratedPageIdRef` (shared, above the fork): the
 * canvas hooks above the fork belong to the TABLE (GM Studio map panes have
 * their own, package 5.3). While the table is shown its router keeps them
 * live; when the table goes (Studio, another campaign) it hands their content
 * back to the store and releases them (`hydratedPageIdRef = ''`) — nothing
 * maintains them meanwhile, so nothing may save them over the store later.
 * The next table mount loads its page from the store.
 */
export function usePageHydration() {
  const { pages } = useSessionContext();
  const { drawing, fog, dynamicFog, wallsHook, mapObjects } = useCanvasContext();
  const { tokens, notes: notesHook, pins: pinsHook } = useTokenContext();
  const loader = usePageLoader();
  const { loadPageToHooks } = loader;

  // Latest save: the unmount cleanup must not write a mount-time snapshot.
  const saveRef = useRef(loader.saveCurrentPageToStore);
  saveRef.current = loader.saveCurrentPageToStore;
  const hydratedPageIdRef = pages.hydratedPageIdRef;
  useEffect(() => () => {
    saveRef.current();
    hydratedPageIdRef.current = '';
  }, [hydratedPageIdRef]);

  useEffect(() => {
    const prevId = pages.hydratedPageIdRef.current;
    const newId = pages.viewingPageId;
    if (!newId || prevId === newId) return;
    if (prevId && pages.getPageData(prevId)) {
      const existing = pages.getPageData(prevId)!;
      pages.setPageData(prevId, {
        ...existing,
        tokens: tokens.tokens.map((t) => {
          const { imageEl, ...rest } = t;
          return rest;
        }),
        drawings: [...drawing.strokes.current],
        notes: notesHook.notes,
        pins: pinsHook.pins,
        mapObjects: mapObjects.objects.current,
        fog: { enabled: fog.enabled.current, mode: fog.fogModeRef.current, revealed: [...fog.revealed.current], closings: [...fog.closings.current], explored: [...dynamicFog.exploredRef.current] },
        walls: [...wallsHook.walls.current],
      });
    }
    const newPage = pages.getPageData(newId);
    if (newPage) loadPageToHooks(newPage);
  }, [pages.viewingPageId]); // eslint-disable-line react-hooks/exhaustive-deps

  return loader;
}
